import type { AutocompleteApi } from '@algolia/autocomplete-js'
import { useLocale } from '@dreamstack/i18n/src/locale/useLocale'
import { createContext, useCallback, useContext, useRef, useState } from 'react'
import type { InstantSearchProps } from 'react-instantsearch-dom'
import {
  Configure,
  InstantSearch as OriginalInstantSearch,
  SearchBox,
} from 'react-instantsearch-dom'
import 'twin.macro'
import {
  ALGOLIA_INDEX_NAME_LEXICON,
  ALGOLIA_INDEX_NAME_NEWS,
} from '../env.config'
import type { IndexType } from '../lib/types'
import { SearchHitsContextProvider } from './SearchHitsContext'
import { searchClient } from './searchClient'

const useSearchContextState = () => {
  const autocompleteRef = useRef<AutocompleteApi<any>>(null)
  const [searchState, setSearchState] = useState<any>({
    query: '',
  })

  const submitState = useCallback(
    ({ state }: any) => {
      setSearchState((searchState: any) => ({
        ...searchState,
        query: state.query,
      }))
    },
    [setSearchState]
  )

  const resetState = useCallback(() => {
    setSearchState((searchState: any) => ({
      ...searchState,
      query: '',
    }))
    autocompleteRef.current?.setQuery('')
  }, [setSearchState])

  return {
    searchState,
    setSearchState,
    submitState,
    resetState,
    autocompleteRef,
  }
}

type ISearchContext = ReturnType<typeof useSearchContextState>

export const SearchContext = createContext({} as ISearchContext)

/**
 * `InstantSearch` wrapper component.
 *
 * Extends the original `InstantSearch` props to include `children` to avoid TypeScript errors.
 *
 * @param {object} props - The props for the component.
 * @returns {ReactElement} The `InstantSearch` component.
 */
interface MyInstantSearchProps extends InstantSearchProps {
  children?: React.ReactNode
}

const InstantSearch: React.FC<MyInstantSearchProps> = (props) => {
  return (
    <OriginalInstantSearch {...props}>{props.children}</OriginalInstantSearch>
  )
}

export const SearchContextProvider = ({
  children,
  indexType,
}: {
  children: any
  indexType: IndexType
}) => {
  const context = useSearchContextState()
  const { searchState, setSearchState } = context
  const locale = useLocale()
  const indexName =
    indexType === 'lexicon'
      ? ALGOLIA_INDEX_NAME_LEXICON
      : ALGOLIA_INDEX_NAME_NEWS
  return (
    <SearchContext.Provider value={context}>
      <InstantSearch
        searchClient={searchClient}
        indexName={indexName}
        searchState={searchState}
        onSearchStateChange={setSearchState}
      >
        <Configure filters={`locale:${locale}`} />
        {/* SearchBox is hidden because Autocomplete is used. Must be present for things to work though */}
        <SearchBox tw="hidden" />
        <SearchHitsContextProvider>{children}</SearchHitsContextProvider>
      </InstantSearch>
    </SearchContext.Provider>
  )
}

export const useSearchContext = () => {
  return useContext(SearchContext)
}

export const useHasSearchContext = () => {
  const context = useSearchContext()
  return !!context.setSearchState
}

export const useSearchIsSearching = () => {
  const context = useSearchContext()
  return !!context?.searchState?.query
}
